import { getAmountsSaved, isFieldValueWithinLimits } from '../lib/utils'

export type CalculatorAction = FieldAction | InterestAction | ErrorAction

type FieldNames = 'startAmount' | 'savedEachMonth' | 'savingTimeInYears'

interface FieldAction {
  type: 'fieldChange'
  field: FieldNames
  value: number
}

interface InterestAction {
  type: 'interestRateChange'
  value: number
}

interface ErrorAction {
  type: 'error'
}

export interface AppState {
  interestRate: number
  startAmount: number
  savedEachMonth: number
  savingTimeInYears: number
  totalAmount: number
  accumulatedInterest: number
  accumulatedDeposited: number
  hasCalculationError: boolean
}

function oneOrMoreFieldsAreOutsideLimits(state: AppState, field: FieldNames, value: number) {
  const modifiedState = { ...state, [field]: value }
  const fieldValues = [
    { field: 'startAmount', value: modifiedState.startAmount },
    { field: 'savedEachMonth', value: modifiedState.savedEachMonth },
    { field: 'savingTimeInYears', value: modifiedState.savingTimeInYears },
  ]
  return fieldValues.some(
    (fieldValue) => !isFieldValueWithinLimits(fieldValue.field, fieldValue.value),
  )
}

export function performCalculation(state: AppState, field: FieldNames, value: number) {
  if (oneOrMoreFieldsAreOutsideLimits(state, field, value)) {
    return {
      ...state,
      [field]: value,
      hasCalculationError: true,
      totalAmount: 0,
    }
  } else {
    const modifiedState = { ...state, [field]: value }
    const { interestRate, savedEachMonth, startAmount, savingTimeInYears } = modifiedState
    const newStateValues = getAmountsSaved(
      interestRate,
      savedEachMonth,
      startAmount,
      savingTimeInYears,
    )
    return {
      ...state,
      [field]: value,
      hasCalculationError: false,
      ...newStateValues,
    }
  }
}

export function calculatorReducer(state: AppState, action: CalculatorAction) {
  switch (action.type) {
    case 'interestRateChange': {
      const newStateValues = getAmountsSaved(
        action.value,
        state.savedEachMonth,
        state.startAmount,
        state.savingTimeInYears,
      )
      return {
        ...state,
        interestRate: action.value,
        ...newStateValues,
      }
    }
    case 'fieldChange': {
      return performCalculation(state, action.field, action.value)
    }
    case 'error': {
      return {
        ...state,
        hasCalculationError: true,
      }
    }
    default: {
      throw Error('Unknown action: ' + action)
    }
  }
}
